import React from 'react';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import type { TLDDistributionChartProps, TLDDistributionItem } from '../../types/analytics';
import { useThemeStore } from '../../store';

/**
 * Format a number as currency.
 */
function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Color palette for TLD segments.
 */
const COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
];

const OTHER_COLOR = '#9CA3AF'; // gray for "Other" category

/**
 * Custom tooltip component for the chart.
 */
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TLDDistributionItem & { displayName?: string } }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-cap-dark p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <p className="text-sm font-medium text-gray-900 dark:text-warm-white">{data.tld}</p>
        <p className="text-sm text-primary-600 font-semibold">
          {formatCurrency(data.total_amount)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.count} {data.count === 1 ? 'purchase' : 'purchases'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.percentage.toFixed(1)}%
        </p>
      </div>
    );
  }
  return null;
};

/**
 * TLDDistributionChart Component
 *
 * Displays spending distribution by TLD using either a pie chart or bar chart.
 * Supports click interactions for filtering by TLD.
 */
const TLDDistributionChart: React.FC<TLDDistributionChartProps> = ({
  data,
  loading,
  chartType = 'pie',
  onTLDClick,
}) => {
  const isDark = useThemeStore((s) => s.resolvedTheme === 'dark');
  const gridStroke = isDark ? '#374151' : '#E5E7EB';
  const tickFill = isDark ? '#9CA3AF' : '#6B7280';

  // Loading state
  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading TLD distribution chart">
        <div
          data-testid="tld-chart-skeleton"
          className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 h-80 animate-pulse"
        >
          <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
          <div className="flex justify-center items-center h-56">
            <div className="w-48 h-48 bg-gray-100 dark:bg-gray-700 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  // Empty or null data state
  if (!data || data.distributions.length === 0) {
    return (
      <div
        data-testid="tld-distribution-chart"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4 h-80 flex flex-col items-center justify-center"
      >
        <p className="text-gray-500 dark:text-gray-400 font-medium">No TLD data</p>
        <p className="text-gray-400 text-sm mt-1">No purchases in selected period</p>
      </div>
    );
  }

  const { distributions, other } = data;

  // Combine distributions with "other" if present
  const chartData: (TLDDistributionItem & { displayName?: string })[] = [
    ...distributions,
  ];

  if (other) {
    chartData.push({
      ...other,
      displayName: `Other (${other.count} TLDs)`,
    });
  }

  // Handle segment click
  const handleClick = (tld: string) => {
    if (onTLDClick) {
      onTLDClick(tld);
    }
  };

  // Get color for a TLD
  const getColor = (index: number, tld: string) => {
    if (tld === 'other') return OTHER_COLOR;
    return COLORS[index % COLORS.length];
  };

  return (
    <div data-testid="tld-distribution-chart" className="bg-white dark:bg-cap-dark rounded-lg shadow-sm p-4">
      {/* Header */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">TLD Distribution</h3>

      {/* Chart description for accessibility */}
      <div data-testid="chart-description" className="sr-only">
        TLD distribution chart with {distributions.length} TLDs.
      </div>

      {/* Legend with percentages and amounts */}
      <div className="mb-4 flex flex-wrap gap-2">
        {chartData.map((item, index) => (
          <button
            key={item.tld}
            onClick={() => handleClick(item.tld)}
            style={{ cursor: onTLDClick ? 'pointer' : 'default' }}
            data-testid={`segment-${item.tld.replace('.', '')}`}
            className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getColor(index, item.tld) }}
            />
            <span className="font-medium">{item.displayName || item.tld}</span>
            <span className="text-gray-500 dark:text-gray-400">{item.percentage.toFixed(1)}%</span>
          </button>
        ))}
      </div>

      {/* Chart container */}
      <div
        className="h-64"
        role="img"
        aria-label="TLD distribution chart"
      >
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <XAxis
                dataKey="tld"
                tick={{ fontSize: 12, fill: tickFill }}
                stroke={gridStroke}
              />
              <YAxis
                tickFormatter={(value) => `$${value}`}
                tick={{ fontSize: 12, fill: tickFill }}
                stroke={gridStroke}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="total_amount"
                onClick={(data: unknown) => {
                  const typedData = data as { tld?: string };
                  if (typedData.tld) handleClick(typedData.tld);
                }}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.tld}`}
                    data-testid={entry.tld === 'other' ? 'cell-other' : 'cell'}
                    fill={getColor(index, entry.tld)}
                    style={{ cursor: onTLDClick ? 'pointer' : 'default' }}
                  />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <PieChart>
              <Pie
                data={chartData as unknown as Array<{ [key: string]: unknown }>}
                dataKey="total_amount"
                nameKey="tld"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                onClick={(data: { tld: string }) => handleClick(data.tld)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.tld}`}
                    data-testid={entry.tld === 'other' ? 'cell-other' : 'cell'}
                    fill={getColor(index, entry.tld)}
                    style={{ cursor: onTLDClick ? 'pointer' : 'default' }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TLDDistributionChart;
